import { useEffect, useState } from "react";
import classes from "./InfoQuestion.module.css";
import InfoQuestion from "../../../ts/models/InfoQuestion";
import AddInfoQuestion from "./AddInfoQuestion";
import PanelActionContainer from "../../../components/Containers/PanelActionContainer";
import PanelContainer from "../../../components/Containers/PanelContainer";
import SearchBar from "../../../components/SearchBar/SearchBar";
import PrimaryButton from "../../../components/Buttons/PrimaryButton";
import PanelInnerContainer from "../../../components/Containers/PanelInnerContainer";
import TableContainer from "../../../components/CustTable/TableContainer";
import Table from "../../../components/CustTable/Table";
import ActionContainer from "../../../components/CustTable/ActionContainer";
import LinkButton from "../../../components/Buttons/LinkButton";
import { Column } from "../../../ts/types";
import {
  fetchInfoQuestions,
  fetchInfoQuestionsCount,
  updateInfoQuestion,
} from "../../../services/masters";
import addIcon from "../../../assets/icons/add-circle-white.svg";
import { InfoQuestionType } from "../../../ts/enums";
import Select from "../../../components/Inputs/Select";
import { status } from "../../../ts/consts";
import { getClasses } from "../../../utils";

const pageSize = 20;

const infoQuestionTypes = [
  {
    name: "Option List",
    type: InfoQuestionType.OptionList,
  },
  {
    name: "Free Text",
    type: InfoQuestionType.FreeText,
  },
  {
    name: "Number",
    type: InfoQuestionType.Number,
  },
  {
    name: "Date",
    type: InfoQuestionType.Date,
  },
];

const InfoQuestions = () => {
  const [show, setShow] = useState(false);
  const [isEditQues, setIsEditQues] = useState(false);
  const [toBeEdited, setToBeEdited] = useState<InfoQuestion>(
    new InfoQuestion()
  );
  const [searchText, setSearchText] = useState("");
  const [infoQuestionData, setInfoQuestionData] = useState<InfoQuestion[]>([]);
  const [currPageNo, setCurrPageNo] = useState(1);
  const [displayCurrPageNo, setDisplayCurrPageNo] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [skipCount, setSkipCount] = useState(true);
  const [allInfoQuestions, setAllInfoQuestions] = useState<InfoQuestion[]>([]);
  const [isActive, setIsActive] = useState("active");

  useEffect(() => {
    fetchInfoQuestionData();
  }, [currPageNo]);

  useEffect(() => {
    if (currPageNo === 1) {
      // Preventing first time auto api call because it is already called from currPageNo useEffect
      if (skipCount) {
        setSkipCount(false);
      } else {
        fetchInfoQuestionData();
      }
    } else {
      // We need to go to page no. 1 if search text change, as well as it will fetch data
      setCurrPageNo(1);
      setDisplayCurrPageNo(1);
    }
  }, [searchText, isActive]);

  const fetchInfoQuestionData = async () => {
    let filter: any = {
      limit: pageSize,
      offset: (currPageNo - 1) * pageSize,
      where: {
        $or: [
          {
            code: {
              $regex: `.*${searchText}.*`,
              $options: "i",
            },
          },

          {
            question: {
              $regex: `.*${searchText}.*`,
              $options: "i",
            },
          },
        ],
      },
    };

    if (isActive === "active") {
      filter.where.isActive = true;
    } else if (isActive === "inactive") {
      filter.where.isActive = false;
    }

    try {
      const [infoQuestionRes, countRes] = await Promise.all([
        fetchInfoQuestions(filter),
        fetchInfoQuestionsCount(filter.where),
      ]);
      if (infoQuestionRes?.status === 200) {
        setInfoQuestionData(infoQuestionRes.data);
        setDisplayCurrPageNo(currPageNo);
      }
      if (countRes?.status === 200) {
        const noOfPages = Math.ceil(Number(countRes.data.count) / pageSize);
        setTotalPages(noOfPages);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchAllInfoQuestions = async () => {
    try {
      const infoQuestionRes = await fetchInfoQuestions({ isActive: true });
      if (infoQuestionRes.status === 200) {
        setAllInfoQuestions(infoQuestionRes.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchAllInfoQuestions();
  }, []);

  const InfoQuestionTableColumn: Column<InfoQuestion>[] = [
    {
      header: "ID",
      accessor: (e, idx) => (displayCurrPageNo - 1) * pageSize + idx! + 1,
      width: "10%",
    },
    {
      header: "CODE",
      accessor: (e) => e.code,
      width: "10%",
    },
    {
      header: "QUESTION",
      accessor: (e) => e.question,
    },
    {
      header: "TYPE",
      accessor: (e) =>
        infoQuestionTypes.find(
          (questionType) => questionType.type === e.questionType
        )?.name || "",
      width: "10%",
    },
    {
      header: "OPTIONS",
      accessor: (e) => (
        <div>
          {e.questionType !== InfoQuestionType.OptionList && (
            <div
              className={getClasses(
                classes.questionType,
                classes.notApplicable
              )}>
              NA
            </div>
          )}
          {e.options.map((item, idx) => (
            <div key={idx}>
              {`${idx + 1}. `}
              {item}
            </div>
          ))}
        </div>
      ),
      width: "10%",
    },
    {
      header: "ACTIVE",
      accessor: (e) => (e.isActive ? "YES" : "NO"),
      style: { textAlign: "center" },
    },
    {
      header: "ACTION",
      accessor: (e) => (
        <ActionContainer>
          <LinkButton
            onClick={() => {
              editHandler(e);
            }}
            label='Edit'
            className={classes.editAction}
          />
          <LinkButton
            onClick={() => updateStatusHandler(e.id, !e.isActive)}
            label={e.isActive ? "De-activate" : "Activate"}
          />
        </ActionContainer>
      ),
      width: "15%",
    },
  ];

  const updateStatusHandler = (id: string, isActive: boolean) => {
    updateInfoQuestion({ id, isActive }).then((res) => {
      if (res.status === 200) {
        fetchInfoQuestionData();
      }
    });
  };

  const addShowHandle = () => {
    setShow(true);
    setToBeEdited(new InfoQuestion());
  };

  const editHandler = (data: InfoQuestion) => {
    setShow(true);
    setIsEditQues(true);
    setToBeEdited(data);
  };

  const closeHandler = (isUpdate: boolean) => {
    setShow(false);
    setIsEditQues(false);
    if (isUpdate) {
      fetchInfoQuestionData();
      fetchAllInfoQuestions();
    }
  };

  return (
    <>
      {show && (
        <AddInfoQuestion
          onClose={closeHandler}
          isEdit={isEditQues}
          prevData={toBeEdited}
          allInfoQuestions={allInfoQuestions}
        />
      )}
      <PanelContainer name='Info Questions' backBtn>
        <PanelActionContainer
          leftChildren={
            <div className={classes.left}>
              <SearchBar
                debounce
                onChange={(text) => {
                  setSearchText(text);
                }}
              />
              <Select
                className={classes.statusSelect}
                selectContainerClass={classes.selectContainer}
                label='Show:'
                value={isActive}
                defaultOption={"Active"}
                options={status}
                onChange={(e) => setIsActive(e.target.value)}
                htmlFor={"status"}
                nameExtractor={"name"}
                valueExtractor='value'
              />
            </div>
          }
          rightChildren={
            <PrimaryButton
              name='Add New'
              onClick={addShowHandle}
              disabled={false}
              icon={<img src={addIcon} alt='' />}
            />
          }
        />
        <PanelInnerContainer>
          <TableContainer>
            <Table
              columns={InfoQuestionTableColumn}
              data={infoQuestionData}
              isPaginated
              pageNo={displayCurrPageNo}
              totalPages={totalPages}
              onPrevClick={() => setCurrPageNo((state) => state - 1)}
              onNextClick={() => setCurrPageNo((state) => state + 1)}
            />
          </TableContainer>
        </PanelInnerContainer>
      </PanelContainer>
    </>
  );
};

export default InfoQuestions;
